// src/components/TransactionHistory.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  CalendarDays,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

import DashboardLayout from "./DashboardLayout";
import { clearAuthUser, getAuthUser } from "../utils/auth";
import { apiFetch, getApiBaseUrl } from "../utils/apiClient";

const formatFullDate = (date) =>
  date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const formatShortDate = (date) =>
  date.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export default function TransactionHistory() {
  const navigate = useNavigate();
  const user = useMemo(() => getAuthUser(), []);

  const handleSignOut = () => {
    clearAuthUser();
    navigate("/login", { replace: true });
  };

  const [tab, setTab] = useState("onchain");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [page, setPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);
  const pageSize = 12;
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let ignore = false;

    const loadTransactions = async () => {
      try {
        const response = await apiFetch("/transactions");
        if (!ignore && response?.transactions) {
          setTransactions(response.transactions);
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.data?.message ||
              apiError.message ||
              "Failed to load transactions.",
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadTransactions();

    return () => {
      ignore = true;
    };
  }, []);

  const mappedTransactions = useMemo(
    () =>
      transactions.map((transaction) => {
        const createdAt = transaction.createdAt
          ? new Date(transaction.createdAt)
          : new Date();
        const discountRaw = transaction.metadata?.referralDiscount;
        const discount = Number.isFinite(discountRaw)
          ? discountRaw
          : Number(discountRaw);

        return {
          id: transaction.id || transaction._id,
          packageName: transaction.packageName || "SynergyCoin Package",
          amount: Number(transaction.amount) || 0,
          status: transaction.status || "completed",
          date: createdAt,
          discount: Number.isFinite(discount) ? discount : 0,
        };
      }),
    [transactions],
  );

  const onChainRows = mappedTransactions;
  const tokenRows = useMemo(
    () => mappedTransactions.filter((row) => row.discount > 0),
    [mappedTransactions],
  );

  const dataset = tab === "onchain" ? onChainRows : tokenRows;

  const handleDownloadInvoice = async (transactionId) => {
    try {
      setDownloadingId(transactionId);

      const baseUrl = getApiBaseUrl();
      const authUser = getAuthUser();
      const userId =
        authUser?.id ||
        (typeof authUser?._id === "string" ? authUser._id : undefined);

      const headers = {};
      if (userId) {
        headers["x-user-id"] = userId.toString();
      }

      const response = await fetch(
        `${baseUrl}/transactions/${transactionId}/receipt`,
        {
          method: "GET",
          headers,
        },
      );

      if (!response.ok) {
        // Try to extract error message from backend for easier debugging
        let errorMessage = "Failed to download invoice";
        try {
          const errorData = await response.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = `Failed to download invoice (status ${response.status})`;
          }
        } catch {
          errorMessage = `Failed to download invoice (status ${response.status})`;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `receipt-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      // Basic error feedback; could be replaced with toast system
      // eslint-disable-next-line no-alert
      alert(
        downloadError?.message ||
          "Failed to download invoice. Please try again later.",
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = useMemo(() => {
    let rows = dataset;
    if (query.trim()) {
      const needle = query.trim().toLowerCase();
      rows = rows.filter((row) =>
        row.packageName.toLowerCase().includes(needle),
      );
    }

    if (dateRange.start || dateRange.end) {
      rows = rows.filter((row) => {
        const timestamp = row.date.getTime();
        if (dateRange.start) {
          const start = new Date(dateRange.start);
          start.setHours(0, 0, 0, 0);
          if (timestamp < start.getTime()) {
            return false;
          }
        }
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          if (timestamp > endDate.getTime()) {
            return false;
          }
        }
        return true;
      });
    }

    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === "package") {
        return a.packageName.localeCompare(b.packageName) * dir;
      }
      if (sort.key === "status") {
        return a.status.localeCompare(b.status) * dir;
      }
      if (sort.key === "amount") {
        return (a.amount - b.amount) * dir;
      }
      return (a.date.getTime() - b.date.getTime()) * dir;
    });
  }, [dataset, query, sort, dateRange]);

  useEffect(() => {
    setPage(1);
  }, [query, tab, dateRange]);

  useEffect(() => {
    if (!isDatePickerOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const setSortKey = (key) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );

  const SortButton = ({ sortKey, children }) => (
    <button
      type="button"
      onClick={() => setSortKey(sortKey)}
      className="inline-flex items-center gap-1 text-left text-sm text-white/70 transition hover:text-white"
    >
      {children}
      <span
        className={`text-[10px] leading-none ${
          sort.key === sortKey ? "opacity-100" : "opacity-30"
        }`}
      >
        {sort.key === sortKey ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );

  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, total);

  const clearDateRange = () => {
    setDateRange({ start: "", end: "" });
    setIsDatePickerOpen(false);
  };

  const dateFilterLabel = useMemo(() => {
    const formatDisplayDate = (value) => {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      return parsed.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    if (!dateRange.start && !dateRange.end) return "Select dates";
    if (dateRange.start && dateRange.end) {
      return `${formatDisplayDate(dateRange.start)} – ${formatDisplayDate(dateRange.end)}`;
    }
    if (dateRange.start) {
      return `Since ${formatDisplayDate(dateRange.start)}`;
    }
    return `Until ${formatDisplayDate(dateRange.end)}`;
  }, [dateRange]);

  return (
    <DashboardLayout
      user={user}
      onSignOut={handleSignOut}
      activeKey="transactions"
    >
      {({ displayName, signOut }) => (
        <>
          <div className="mb-6 text-sm text-white/50">
            Dashboard <span className="mx-2 text-white/30">›</span> Transaction
            History
          </div>

          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                Transaction History
              </h1>
              <p className="mt-2 text-sm text-white/60">
                Track and review every movement tied to your account.
              </p>
            </div>

            {/* <button
              type="button"
              onClick={signOut}
              className="hidden rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10 sm:inline-flex"
            >
              Sign out
            </button> */}
          </div>

          <div className="mb-6 flex items-center gap-3 sm:grid sm:grid-cols-[minmax(0,1fr),auto] sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2.5">
              <Search size={18} className="text-white/50" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${displayName}'s transactions`}
                className="w-full bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
                type="search"
              />
            </div>

            <div className="relative flex-shrink-0" ref={datePickerRef}>
              <button
                type="button"
                onClick={() => setIsDatePickerOpen((value) => !value)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/70 transition hover:bg-white/10"
                title="Filter by date"
              >
                <CalendarDays size={18} className="text-white/80" />
                <span className="hidden sm:inline">{dateFilterLabel}</span>
              </button>

              {isDatePickerOpen && (
                <div className="absolute right-0 z-20 mt-2 w-[260px] rounded-2xl border border-white/10 bg-[#111213] p-4 text-sm text-white/80 shadow-[0_20px_45px_rgba(0,0,0,0.45)]">
                  <div className="flex flex-col gap-3">
                    <label className="space-y-1">
                      <span className="text-xs uppercase tracking-[0.3em] text-white/40">
                        Start date
                      </span>
                      <input
                        type="date"
                        value={dateRange.start}
                        max={dateRange.end || undefined}
                        onChange={(event) =>
                          setDateRange((prev) => ({ ...prev, start: event.target.value }))
                        }
                        className="w-full rounded-xl border border-white/10 bg-[#1A1B1F] px-3 py-2 text-sm text-white focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/35"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs uppercase tracking-[0.3em] text-white/40">
                        End date
                      </span>
                      <input
                        type="date"
                        value={dateRange.end}
                        min={dateRange.start || undefined}
                        onChange={(event) =>
                          setDateRange((prev) => ({ ...prev, end: event.target.value }))
                        }
                        className="w-full rounded-xl border border-white/10 bg-[#1A1B1F] px-3 py-2 text-sm text-white focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/35"
                      />
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={clearDateRange}
                        className="rounded-lg border border-white/10 bg-transparent px-3 py-2 text-xs font-semibold text-white/60 transition hover:text-white"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsDatePickerOpen(false)}
                        className="rounded-lg bg-[#8C3CDD] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#A367FF]"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setTab("onchain")}
              className={`rounded-full px-4 py-2 text-sm ${
                tab === "onchain"
                  ? "bg-[#2A1F36] text-white"
                  : "bg-white/5 text-white/70 transition hover:text-white"
              }`}
            >
              All Purchases
            </button>
            <button
              type="button"
              onClick={() => setTab("token")}
              className={`rounded-full px-4 py-2 text-sm ${
                tab === "token"
                  ? "bg-[#2A1F36] text-white"
                  : "bg-white/5 text-white/70 transition hover:text-white"
              }`}
            >
              Referral Savings
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-white/10">
            <div className="hidden min-w-full overflow-x-auto md:block">
              <table className="min-w-full bg-black">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="px-5 py-3">
                      <SortButton sortKey="package">Package</SortButton>
                    </th>
                    <th className="px-5 py-3">
                      <SortButton sortKey="amount">Purchase</SortButton>
                    </th>
                    <th className="px-5 py-3">
                      <SortButton sortKey="status">Status</SortButton>
                    </th>
                    <th className="px-5 py-3">
                      <SortButton sortKey="date">Date</SortButton>
                    </th>
                    <th className="px-5 py-3 text-sm font-semibold text-white/70">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-10 text-center text-sm text-white/60"
                      >
                        Loading transactions…
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    pageRows.map((row) => (
                      <tr key={row.id} className="border-b border-white/10">
                        <td className="px-5 py-4 text-sm text-white/90">
                          {row.packageName}
                        </td>
                        <td className="px-5 py-4 text-sm">
                        $
                        {row.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-5 py-4 text-sm capitalize text-white/70">
                        {row.status}
                      </td>
                      <td className="px-5 py-4 text-sm">
                        {formatFullDate(row.date)}
                      </td>
                      <td className="px-5 py-4 text-sm">
                        <button
                          type="button"
                          onClick={() => handleDownloadInvoice(row.id)}
                          disabled={downloadingId === row.id}
                          className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {downloadingId === row.id ? "Downloading…" : "Download"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && pageRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-10 text-center text-sm text-white/50"
                      >
                        No transactions match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid gap-4 p-4 md:hidden">
              {loading && (
                <div className="rounded-xl border border-white/10 bg-[#121214] p-6 text-center text-sm text-white/60">
                  Loading transactions…
                </div>
              )}
              {!loading &&
                pageRows.map((row) => (
                  <div
                    key={`card-${row.id}`}
                    className="space-y-2 rounded-xl border border-white/10 bg-[#121214] p-4 shadow-[0_6px_24px_rgba(0,0,0,0.45)]"
                  >
                    <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-white">
                      {row.packageName}
                    </span>
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/70">
                      $
                      {row.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="text-xs text-white/60">
                    {formatShortDate(row.date)}
                  </div>
                  <div className="text-xs text-white/50 capitalize">
                    Status: {row.status}
                  </div>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoice(row.id)}
                      disabled={downloadingId === row.id}
                      className="w-full rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {downloadingId === row.id
                        ? "Downloading…"
                        : "Download invoice"}
                    </button>
                  </div>
                </div>
                ))}
              {!loading && pageRows.length === 0 && (
                <div className="rounded-xl border border-white/10 bg-[#121214] p-6 text-center text-sm text-white/60">
                  No transactions match your filters.
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {total === 0
                ? "No results"
                : `Showing ${startRow} to ${endRow} out of ${total} results`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/70 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft size={16} />
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(totalPages, current + 1))
                }
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/70 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                Next
                <ChevronRight size={16} />
              </button>
              
            </div>
                <div className="pb-24 sm:pb-0"></div>

          </div>
        </>
      )}
    </DashboardLayout>
  );
}
